import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import * as Sentry from '@sentry/react'
import { parseApiError } from '@/lib/errors'
import type {
  AccountInvite,
  PasswordTokenInspection,
  ActPayload,
  ActResult,
  ActivityEntry,
  AttachmentKind,
  AttachmentMeta,
  ApplicantDetail,
  CreateApplicantPayload,
  CreateRequisitionPayload,
  CreateTrackerPayload,
  ItemStatusPayload,
  ItemStatusResult,
  OnboardingBoard,
  OnboardingBoardItem,
  OnboardingTrackerStatus,
  RecruitmentBoard,
  RequisitionBrief,
  RequisitionDetail,
  RequisitionStatus,
  StageChangePayload,
  StageChangeResult,
  TrackerBrief,
  TrackerDetail,
  UpdateApplicantPayload,
  UpdateRequisitionPayload,
  UpdateTrackerPayload,
  AuditEntry,
  Booking,
  BookingStatus,
  SchedulerData,
  CalendarEvent,
  CategoryCount,
  Comment,
  CreateBookingPayload,
  CreateCalendarEventPayload,
  CreateIncidentPayload,
  CreateRequestPayload,
  CreateTaskPayload,
  CreateTrainingPayload,
  CreateTravelPayload,
  DashboardSummary,
  Department,
  DepartmentCount,
  EmployeeBrief,
  EmployeeDetail,
  HandlerDetail,
  HandlerStat,
  Incident,
  IncidentCaseKind,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
  MeetingRoom,
  MyApprovalItem,
  NotificationItem,
  OrgSettings,
  Paginated,
  Priority,
  PriorityCount,
  ReportExportFormat,
  ReportFilters,
  ReportResult,
  ReportType,
  RequestBrief,
  RequestDetail,
  RequestListFilters,
  RequestStatus,
  RequestCategory,
  RequestType,
  RoomAvailability,
  SettingsResponse,
  SlaOverview,
  Task,
  TaskStatus,
  Training,
  TrainingStatus,
  TrainingType,
  TransportMode,
  Travel,
  UpdateNotificationPrefsPayload,
  UpdateProfilePayload,
  TravelStatus,
  UserOption,
  Workflow,
  VolumeDay,
} from '@/lib/types'
import type {
  AssetAttachment,
  AssetDetail,
  AssetBrief,
  AssetHistoryData,
  AssetListFilters,
  AssetsSummary,
  AssignAssetPayload,
  AssignmentBrief,
  AssignmentDetail,
  CompleteReservationPayload,
  CreateAssetPayload,
  CreateReservationPayload,
  CreateVehiclePayload,
  DecideReservationPayload,
  ReservationListFilters,
  ReturnAssetPayload,
  UpdateAssetPayload,
  UpdateAssignmentPayload,
  UpdateVehiclePayload,
  VehicleAvailability,
  VehicleBrief,
  VehicleReservation,
} from '@/lib/types'

// ── Axios instance ────────────────────────────────────────────────────────────

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  timeout: 30_000,
  withCredentials: true, // Flask session cookies
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Request interceptor: CSRF token + dev logging ─────────────────────────────

// Distinct cookie name so HRAD and PMO don't clobber each other when both are
// served from the same hostname on different ports. Override at build time via
// `VITE_CSRF_COOKIE_NAME` if you ever change the backend's CSRF_COOKIE_NAME.
const CSRF_COOKIE_NAME = (import.meta.env.VITE_CSRF_COOKIE_NAME as string | undefined) ?? 'hrad_csrf'
const CSRF_HEADER_NAME = 'X-CSRF-Token'
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete'])

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(name + '='))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (UNSAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE_NAME)
    if (token) {
      config.headers = config.headers ?? {}
      ;(config.headers as Record<string, string>)[CSRF_HEADER_NAME] = token
    }
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(`[API] ${config.method?.toUpperCase()} ${config.url}`)
  }
  return config
})

// ── Response interceptor ──────────────────────────────────────────────────────
// Unwrap envelope: { data: ..., request_id: ... } → just the `data` field.
// Paginated: { data: [...], pagination: {...} } → { items: [...], pagination }

api.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data as unknown
    if (
      typeof body === 'object' &&
      body !== null &&
      !Array.isArray(body) &&
      'data' in body
    ) {
      const obj = body as Record<string, unknown>
      if ('pagination' in obj) {
        response.data = { items: obj.data, pagination: obj.pagination }
      } else {
        response.data = obj.data
      }
    }
    return response
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      if (status === 401) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
      }
      // Capture non-4xx errors (5xx + network failures) in Sentry.
      // 4xx are expected client/auth/validation errors — skip to avoid noise.
      const isClientError = status !== undefined && status >= 400 && status < 500
      if (!isClientError) {
        try {
          Sentry.withScope((scope) => {
            scope.setTag('axios.url', error.config?.url ?? 'unknown')
            scope.setTag('axios.method', error.config?.method ?? 'unknown')
            scope.setTag('http.status_code', String(status ?? 'network_error'))
            Sentry.captureException(error)
          })
        } catch {
          // Sentry uninitialised or throws — never break the interceptor
        }
      }
    }
    return Promise.reject(parseApiError(error))
  },
)

// ── Auth-related types ────────────────────────────────────────────────────────

export type UserRole = 'admin' | 'manager' | 'handler' | 'employee'

export interface AuthUser {
  id: number
  email: string
  role: UserRole
  full_name: string | null
  employee_id: number | null
  is_active: boolean
  last_login_at: string | null
  must_change_password?: boolean
}

export interface LoginResponseData {
  user: AuthUser
  session_expires_at: string
}

// Unauthenticated-safe config the login page polls to decide whether to
// render "Sign in with Microsoft" — see backend `GET /auth/config`.
export interface AuthConfig {
  microsoft_sso_enabled: boolean
}

// ── Typed API functions ───────────────────────────────────────────────────────

export const auth = {
  config: () => api.get<AuthConfig>('/api/v1/auth/config'),

  login: (email: string, password: string) =>
    api.post<LoginResponseData>('/api/v1/auth/login', { email, password }),

  logout: () => api.post<void>('/api/v1/auth/logout'),

  me: () => api.get<AuthUser>('/api/v1/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  requestPasswordReset: (email: string) =>
    api.post<void>('/api/v1/auth/password-reset/request', { email }),

  inspectPasswordToken: (raw: string) =>
    api.get<PasswordTokenInspection>(`/api/v1/auth/password-tokens/${encodeURIComponent(raw)}`),

  confirmPasswordToken: (raw: string, newPassword: string) =>
    api.post<void>(`/api/v1/auth/password-tokens/${encodeURIComponent(raw)}/confirm`, {
      new_password: newPassword,
    }),
}

export const health = {
  check: () => api.get<{ status: string }>('/api/v1/health'),
}

// ── Employees ─────────────────────────────────────────────────────────────────

export const employees = {
  list: (params?: { q?: string; department_id?: number; status?: string; page?: number; page_size?: number }) =>
    api.get<Paginated<EmployeeBrief>>('/api/v1/employees', { params }),
  get: (id: number) => api.get<EmployeeDetail>(`/api/v1/employees/${id}`),
  update: (id: number, payload: { is_managerial: boolean }) =>
    api.patch<EmployeeDetail>(`/api/v1/employees/${id}`, payload),
  createAccount: (id: number, payload: { email?: string }) =>
    api.post<AccountInvite>(`/api/v1/employees/${id}/account`, payload),
  reinvite: (id: number) => api.post<AccountInvite>(`/api/v1/employees/${id}/account/invite`),
}

// ── Handlers (HRAD team) ──────────────────────────────────────────────────────

export const handlers = {
  list: () => api.get<HandlerDetail[]>('/api/v1/handlers'),
  get: (id: number) => api.get<HandlerDetail>(`/api/v1/handlers/${id}`),
}

// ── Requests ──────────────────────────────────────────────────────────────────

export const requests = {
  list: (filters?: RequestListFilters) =>
    api.get<Paginated<RequestBrief>>('/api/v1/requests', { params: filters }),

  get: (id: number) => api.get<RequestDetail>(`/api/v1/requests/${id}`),

  create: (payload: CreateRequestPayload) =>
    api.post<RequestDetail>('/api/v1/requests', payload),

  update: (id: number, payload: { title?: string; description?: string; priority?: Priority }) =>
    api.patch<RequestDetail>(`/api/v1/requests/${id}`, payload),

  changeStatus: (id: number, payload: { status: RequestStatus; reason?: string }) =>
    api.post<RequestDetail>(`/api/v1/requests/${id}/status`, payload),

  reassign: (id: number, handlerUserId: number | null) =>
    api.post<RequestDetail>(`/api/v1/requests/${id}/reassign`, { handler_user_id: handlerUserId }),

  listComments: (id: number) => api.get<Comment[]>(`/api/v1/requests/${id}/comments`),

  addComment: (id: number, body: string) =>
    api.post<Comment>(`/api/v1/requests/${id}/comments`, { body }),

  listAudit: (id: number) => api.get<AuditEntry[]>(`/api/v1/requests/${id}/audit`),

  uploadAttachment: (id: number, file: File, kind?: AttachmentKind) => {
    const form = new FormData()
    form.append('file', file)
    if (kind) form.append('kind', kind)
    return api.post<AttachmentMeta>(`/api/v1/requests/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  listAttachments: (id: number) => api.get<AttachmentMeta[]>(`/api/v1/requests/${id}/attachments`),

  workflow: (id: number) => api.get<Workflow>(`/api/v1/requests/${id}/workflow`),
}

// ── Workflows / org ───────────────────────────────────────────────────────────

export const workflows = {
  act: (stepId: number, payload: ActPayload) =>
    api.post<ActResult>(`/api/v1/workflows/steps/${stepId}/act`, payload),
  myApprovals: (params?: { page?: number; page_size?: number }) =>
    api.get<Paginated<MyApprovalItem>>('/api/v1/workflows/my-approvals', { params }),
}

export const orgSettings = {
  get: () => api.get<OrgSettings>('/api/v1/org-settings'),
  update: (payload: { coo_user_id?: number | null; ceo_user_id?: number | null }) =>
    api.put<OrgSettings>('/api/v1/org-settings', payload),
}

export const departments = {
  list: () => api.get<Department[]>('/api/v1/departments'),
  update: (id: number, payload: { head_user_id?: number | null; is_active?: boolean }) =>
    api.patch<Department>(`/api/v1/departments/${id}`, payload),
}

export interface AdminUserRow {
  id: number
  email: string
  full_name: string | null
  role: UserRole
  is_active: boolean
  sso_linked: boolean
}

export const users = {
  list: (params?: { q?: string }) => api.get<AdminUserRow[]>('/api/v1/users', { params }),
  setRole: (id: number, role: UserRole) =>
    api.patch<AdminUserRow>(`/api/v1/users/${id}/role`, { role }),
  options: (params?: { q?: string; limit?: number }) =>
    api.get<UserOption[]>('/api/v1/users/options', { params }),
}

export const requestTypes = {
  list: (category?: RequestCategory) =>
    api.get<RequestType[]>('/api/v1/requests/types', { params: category ? { category } : undefined }),
  get: (id: number) => api.get<RequestType>(`/api/v1/requests/types/${id}`),
}

export const attachmentUrl = (attachmentId: number): string =>
  `/api/v1/attachments/${attachmentId}`

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const dashboard = {
  summary: () => api.get<DashboardSummary>('/api/v1/dashboard/summary'),
  requestVolume: (days = 14) =>
    api.get<VolumeDay[]>('/api/v1/dashboard/request-volume', { params: { days } }),
  categoryMix: () => api.get<CategoryCount[]>('/api/v1/dashboard/category-mix'),
  priorityMix: () => api.get<PriorityCount[]>('/api/v1/dashboard/priority-mix'),
  departmentMix: () => api.get<DepartmentCount[]>('/api/v1/dashboard/department-mix'),
  topHandlers: (limit = 8) =>
    api.get<HandlerStat[]>('/api/v1/dashboard/top-handlers', { params: { limit } }),
  slaOverview: () => api.get<SlaOverview>('/api/v1/dashboard/sla-overview'),
  activity: (limit = 20) =>
    api.get<ActivityEntry[]>('/api/v1/dashboard/activity', { params: { limit } }),
  recentEscalations: (limit = 10) =>
    api.get<RequestBrief[]>('/api/v1/dashboard/recent-escalations', { params: { limit } }),
}

// ── Notifications ─────────────────────────────────────────────────────────────

export const notifications = {
  list: (params?: { only_unread?: boolean; page?: number; page_size?: number }) =>
    api.get<Paginated<NotificationItem>>('/api/v1/notifications', { params }),
  unreadCount: () => api.get<{ count: number }>('/api/v1/notifications/unread-count'),
  markRead: (id: number) => api.post<NotificationItem>(`/api/v1/notifications/${id}/read`),
  markAllRead: () => api.post<{ marked: number }>('/api/v1/notifications/read-all'),
}

// ── Calendar ──────────────────────────────────────────────────────────────────

export const calendar = {
  events: (params?: { from?: string; to?: string; type?: string; department_id?: number }) =>
    api.get<CalendarEvent[]>('/api/v1/calendar/events', { params }),
  get: (id: number) => api.get<CalendarEvent>(`/api/v1/calendar/events/${id}`),
  create: (payload: CreateCalendarEventPayload) =>
    api.post<CalendarEvent>('/api/v1/calendar/events', payload),
  update: (id: number, payload: Partial<CreateCalendarEventPayload>) =>
    api.patch<CalendarEvent>(`/api/v1/calendar/events/${id}`, payload),
  delete: (id: number) => api.delete<void>(`/api/v1/calendar/events/${id}`),
}

// ── Meeting rooms + bookings ──────────────────────────────────────────────────

export const meetingRooms = {
  listRooms: () => api.get<MeetingRoom[]>('/api/v1/meeting-rooms/rooms'),
  getRoom: (id: number) => api.get<MeetingRoom>(`/api/v1/meeting-rooms/rooms/${id}`),
  availability: (roomId: number, date: string) =>
    api.get<RoomAvailability>(`/api/v1/meeting-rooms/rooms/${roomId}/availability`, {
      params: { date },
    }),
  listBookings: (params?: {
    date_from?: string
    date_to?: string
    room_id?: number
    status?: BookingStatus
    page?: number
    page_size?: number
  }) => api.get<Paginated<Booking>>('/api/v1/meeting-rooms/bookings', { params }),
  getBooking: (id: number) => api.get<Booking>(`/api/v1/meeting-rooms/bookings/${id}`),
  createBooking: (payload: CreateBookingPayload) =>
    api.post<Booking>('/api/v1/meeting-rooms/bookings', payload),
  updateBooking: (
    id: number,
    payload: Partial<Pick<CreateBookingPayload, 'purpose' | 'attendees' | 'catering' | 'catering_notes' | 'gatepass' | 'gate_passes'>>,
  ) => api.patch<Booking>(`/api/v1/meeting-rooms/bookings/${id}`, payload),
  cancelBooking: (id: number) =>
    api.post<Booking>(`/api/v1/meeting-rooms/bookings/${id}/cancel`),
  approveBooking: (id: number) =>
    api.post<Booking>(`/api/v1/meeting-rooms/bookings/${id}/approve`),
  rejectBooking: (id: number, reason?: string) =>
    api.post<Booking>(`/api/v1/meeting-rooms/bookings/${id}/reject`, { reason }),
  scheduler: (dateFrom: string, dateTo?: string) =>
    api.get<SchedulerData>('/api/v1/meeting-rooms/availability', {
      params: { date_from: dateFrom, date_to: dateTo ?? dateFrom },
    }),
}

// ── Incidents ─────────────────────────────────────────────────────────────────

export const incidents = {
  list: (params?: {
    type?: IncidentType
    case_kind?: IncidentCaseKind
    severity?: IncidentSeverity
    status?: IncidentStatus
    q?: string
    page?: number
    page_size?: number
  }) => api.get<Paginated<Incident>>('/api/v1/incidents', { params }),
  get: (id: number) => api.get<Incident>(`/api/v1/incidents/${id}`),
  pdfUrl: (id: number): string => `/api/v1/incidents/${id}/pdf`,
  nteUrl: (id: number): string => `/api/v1/incidents/${id}/documents/nte.pdf`,
  nodUrl: (id: number): string => `/api/v1/incidents/${id}/documents/nod.pdf`,
  workflow: (id: number) => api.get<Workflow>(`/api/v1/incidents/${id}/workflow`),
  uploadAttachment: (id: number, file: File, kind?: AttachmentKind) => {
    const form = new FormData()
    form.append('file', file)
    if (kind) form.append('kind', kind)
    return api.post<AttachmentMeta>(`/api/v1/incidents/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  listAttachments: (id: number) =>
    api.get<AttachmentMeta[]>(`/api/v1/incidents/${id}/attachments`),
  create: (payload: CreateIncidentPayload) =>
    api.post<Incident>('/api/v1/incidents', payload),
  update: (
    id: number,
    payload: Partial<Pick<Incident, 'title' | 'severity' | 'description' | 'location' | 'persons_involved' | 'witnesses'>> & {
      handler_user_id?: number | null
    },
  ) => api.patch<Incident>(`/api/v1/incidents/${id}`, payload),
  changeStatus: (id: number, payload: { status: IncidentStatus; reason?: string }) =>
    api.post<Incident>(`/api/v1/incidents/${id}/status`, payload),
}

// ── Trainings ─────────────────────────────────────────────────────────────────

export const trainings = {
  list: (params?: {
    type?: TrainingType
    status?: TrainingStatus
    department_id?: number
    requested_by_user_id?: number
    q?: string
    page?: number
    page_size?: number
  }) => api.get<Paginated<Training>>('/api/v1/trainings', { params }),
  get: (id: number) => api.get<Training>(`/api/v1/trainings/${id}`),
  create: (payload: CreateTrainingPayload) =>
    api.post<Training>('/api/v1/trainings', payload),
  update: (id: number, payload: Partial<CreateTrainingPayload>) =>
    api.patch<Training>(`/api/v1/trainings/${id}`, payload),
  approve: (id: number, notes?: string) =>
    api.post<Training>(`/api/v1/trainings/${id}/approve`, { notes }),
  reject: (id: number, reason?: string) =>
    api.post<Training>(`/api/v1/trainings/${id}/reject`, { reason }),
  complete: (id: number) =>
    api.post<Training>(`/api/v1/trainings/${id}/complete`),
  setRated: (id: number, rated: boolean) =>
    api.post<Training>(`/api/v1/trainings/${id}/rated`, { rated }),
}

// ── Travel ────────────────────────────────────────────────────────────────────

export const travel = {
  list: (params?: {
    status?: TravelStatus
    transport?: TransportMode
    department_id?: number
    requested_by_user_id?: number
    date_from?: string
    date_to?: string
    q?: string
    page?: number
    page_size?: number
  }) => api.get<Paginated<Travel>>('/api/v1/travel', { params }),
  get: (id: number) => api.get<Travel>(`/api/v1/travel/${id}`),
  create: (payload: CreateTravelPayload) =>
    api.post<Travel>('/api/v1/travel', payload),
  update: (id: number, payload: Partial<CreateTravelPayload>) =>
    api.patch<Travel>(`/api/v1/travel/${id}`, payload),
  approve: (id: number, notes?: string) =>
    api.post<Travel>(`/api/v1/travel/${id}/approve`, { notes }),
  reject: (id: number, reason?: string) =>
    api.post<Travel>(`/api/v1/travel/${id}/reject`, { reason }),
  complete: (id: number) =>
    api.post<Travel>(`/api/v1/travel/${id}/complete`),
  uploadAttachment: (id: number, file: File, kind?: AttachmentKind) => {
    const form = new FormData()
    form.append('file', file)
    if (kind) form.append('kind', kind)
    return api.post<AttachmentMeta>(`/api/v1/travel/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  listAttachments: (id: number) =>
    api.get<AttachmentMeta[]>(`/api/v1/travel/${id}/attachments`),
}

// ── Tasks (HRAD internal) ─────────────────────────────────────────────────────

export const tasks = {
  list: (params?: {
    assignee_user_id?: number
    status?: TaskStatus
    priority?: Priority
    page?: number
    page_size?: number
  }) => api.get<Paginated<Task>>('/api/v1/tasks', { params }),
  get: (id: number) => api.get<Task>(`/api/v1/tasks/${id}`),
  create: (payload: CreateTaskPayload) => api.post<Task>('/api/v1/tasks', payload),
  update: (
    id: number,
    payload: Partial<Pick<Task, 'title' | 'priority' | 'due_date' | 'notes'>> & {
      assignee_user_id?: number
    },
  ) => api.patch<Task>(`/api/v1/tasks/${id}`, payload),
  complete: (id: number) => api.post<Task>(`/api/v1/tasks/${id}/complete`),
  reopen: (id: number) => api.post<Task>(`/api/v1/tasks/${id}/reopen`),
  delete: (id: number) => api.delete<void>(`/api/v1/tasks/${id}`),
}

// ── Reports ───────────────────────────────────────────────────────────────────

export const reports = {
  generate: (type: ReportType, filters?: ReportFilters) =>
    api.post<ReportResult>('/api/v1/reports/generate', { type, filters: filters ?? {} }),
  exportUrl: (type: ReportType, format: ReportExportFormat, filters?: ReportFilters): string => {
    const params = new URLSearchParams({ type, format })
    if (filters) {
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
      }
    }
    return `/api/v1/reports/export?${params.toString()}`
  },
}

// ── Settings ──────────────────────────────────────────────────────────────────

export const settings = {
  get: () => api.get<SettingsResponse>('/api/v1/settings'),
  updateProfile: (payload: UpdateProfilePayload) =>
    api.patch<SettingsResponse>('/api/v1/settings/profile', payload),
  updateNotifications: (payload: UpdateNotificationPrefsPayload) =>
    api.patch<SettingsResponse>('/api/v1/settings/notifications', payload),
  resetDemoData: () =>
    api.post<{ status: string; reseed: boolean }>('/api/v1/settings/demo-data/reset'),
}

// ── Recruitment (Phase C) ─────────────────────────────────────────────────────

export const recruitment = {
  listRequisitions: (params?: {
    status?: RequisitionStatus
    department_id?: number
    page?: number
    page_size?: number
  }) => api.get<Paginated<RequisitionBrief>>('/api/v1/recruitment/requisitions', { params }),
  getRequisition: (id: number) =>
    api.get<RequisitionDetail>(`/api/v1/recruitment/requisitions/${id}`),
  createRequisition: (payload: CreateRequisitionPayload) =>
    api.post<RequisitionDetail>('/api/v1/recruitment/requisitions', payload),
  updateRequisition: (id: number, payload: UpdateRequisitionPayload) =>
    api.patch<RequisitionDetail>(`/api/v1/recruitment/requisitions/${id}`, payload),
  board: (id: number, includeClosed = true) =>
    api.get<RecruitmentBoard>(`/api/v1/recruitment/requisitions/${id}/board`, {
      params: { include_closed: includeClosed },
    }),
  createApplicant: (payload: CreateApplicantPayload) =>
    api.post<ApplicantDetail>('/api/v1/recruitment/applicants', payload),
  getApplicant: (id: number) => api.get<ApplicantDetail>(`/api/v1/recruitment/applicants/${id}`),
  updateApplicant: (id: number, payload: UpdateApplicantPayload) =>
    api.patch<ApplicantDetail>(`/api/v1/recruitment/applicants/${id}`, payload),
  changeStage: (id: number, payload: StageChangePayload) =>
    api.post<StageChangeResult>(`/api/v1/recruitment/applicants/${id}/stage`, payload),
  history: (id: number) =>
    api.get<AuditEntry[]>(`/api/v1/recruitment/applicants/${id}/history`),
  uploadAttachment: (id: number, file: File, kind?: AttachmentKind) => {
    const form = new FormData()
    form.append('file', file)
    if (kind) form.append('kind', kind)
    return api.post<AttachmentMeta>(`/api/v1/recruitment/applicants/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  listAttachments: (id: number) =>
    api.get<AttachmentMeta[]>(`/api/v1/recruitment/applicants/${id}/attachments`),
}

// ── Onboarding (Phase C) ──────────────────────────────────────────────────────

export const onboarding = {
  listTrackers: (params?: {
    status?: OnboardingTrackerStatus
    mine?: boolean
    page?: number
    page_size?: number
  }) => api.get<Paginated<TrackerBrief>>('/api/v1/onboarding/trackers', { params }),
  getTracker: (id: number) => api.get<TrackerDetail>(`/api/v1/onboarding/trackers/${id}`),
  createTracker: (payload: CreateTrackerPayload) =>
    api.post<TrackerDetail>('/api/v1/onboarding/trackers', payload),
  updateTracker: (id: number, payload: UpdateTrackerPayload) =>
    api.patch<TrackerDetail>(`/api/v1/onboarding/trackers/${id}`, payload),
  setItemStatus: (id: number, payload: ItemStatusPayload) =>
    api.post<ItemStatusResult>(`/api/v1/onboarding/items/${id}/status`, payload),
  board: (params?: { status?: OnboardingTrackerStatus; department_id?: number }) =>
    api.get<OnboardingBoard>('/api/v1/onboarding/board', { params }),
  myItems: (includeDone = false) =>
    api.get<OnboardingBoardItem[]>('/api/v1/onboarding/my-items', {
      params: { include_done: includeDone },
    }),
}

// ── Fixed assets (Phase D) ────────────────────────────────────────────────────

export const assets = {
  list: (params?: AssetListFilters) =>
    api.get<Paginated<AssetBrief>>('/api/v1/assets', { params }),
  get: (id: number) => api.get<AssetDetail>(`/api/v1/assets/${id}`),
  create: (payload: CreateAssetPayload) => api.post<AssetDetail>('/api/v1/assets', payload),
  update: (id: number, payload: UpdateAssetPayload) =>
    api.patch<AssetDetail>(`/api/v1/assets/${id}`, payload),
  summary: () => api.get<AssetsSummary>('/api/v1/assets/summary'),
  history: (id: number) => api.get<AssetHistoryData>(`/api/v1/assets/${id}/history`),
  assign: (id: number, payload: AssignAssetPayload) =>
    api.post<AssignmentDetail>(`/api/v1/assets/${id}/assign`, payload),
  byEmployee: (employeeId: number, includeReturned = false) =>
    api.get<AssignmentBrief[]>(`/api/v1/assets/by-employee/${employeeId}`, {
      params: { include_returned: includeReturned },
    }),
  getAssignment: (id: number) => api.get<AssignmentDetail>(`/api/v1/assets/assignments/${id}`),
  updateAssignment: (id: number, payload: UpdateAssignmentPayload) =>
    api.patch<AssignmentDetail>(`/api/v1/assets/assignments/${id}`, payload),
  returnAssignment: (id: number, payload: ReturnAssetPayload) =>
    api.post<AssignmentDetail>(`/api/v1/assets/assignments/${id}/return`, payload),
  pdfUrl: (assignmentId: number): string =>
    `/api/v1/assets/assignments/${assignmentId}/accountability-form.pdf`,
  uploadAttachment: (id: number, file: File, kind: string) => {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    return api.post<AssetAttachment>(`/api/v1/assets/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  listAttachments: (id: number) =>
    api.get<AssetAttachment[]>(`/api/v1/assets/${id}/attachments`),
}

// ── Vehicles (Phase D) ────────────────────────────────────────────────────────

export const vehicles = {
  list: (includeRetired = false) =>
    api.get<VehicleBrief[]>('/api/v1/vehicles', { params: { include_retired: includeRetired } }),
  get: (id: number) => api.get<VehicleBrief>(`/api/v1/vehicles/${id}`),
  create: (payload: CreateVehiclePayload) => api.post<VehicleBrief>('/api/v1/vehicles', payload),
  update: (id: number, payload: UpdateVehiclePayload) =>
    api.patch<VehicleBrief>(`/api/v1/vehicles/${id}`, payload),
  availability: (from: string, to?: string) =>
    api.get<VehicleAvailability>('/api/v1/vehicles/availability', {
      params: { from, to: to ?? from },
    }),
  listReservations: (params?: ReservationListFilters) =>
    api.get<Paginated<VehicleReservation>>('/api/v1/vehicles/reservations', { params }),
  getReservation: (id: number) =>
    api.get<VehicleReservation>(`/api/v1/vehicles/reservations/${id}`),
  createReservation: (payload: CreateReservationPayload) =>
    api.post<VehicleReservation>('/api/v1/vehicles/reservations', payload),
  decide: (id: number, payload: DecideReservationPayload) =>
    api.post<VehicleReservation>(`/api/v1/vehicles/reservations/${id}/decide`, payload),
  cancel: (id: number) =>
    api.post<VehicleReservation>(`/api/v1/vehicles/reservations/${id}/cancel`),
  complete: (id: number, payload: CompleteReservationPayload) =>
    api.post<VehicleReservation>(`/api/v1/vehicles/reservations/${id}/complete`, payload),
}
